import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { Close, Send, Person, Refresh } from '@mui/icons-material';
import { useSendChatMessageMutation } from '../../data/api/endPointsSlice';
import ChatIcon from '@mui/icons-material/Chat';
import MarkdownRenderer from '../markdownRenderer/MarkdownRenderer';
import { ApiError, showSnackbarErrorMessage } from '../utiles/Utiles';
import RedditIcon from '@mui/icons-material/Reddit';

// Chatbot types and interfaces
export interface ChatMessage {
  text: string;
  sender: 'user' | 'bot';
}

const AgentFinderChatBot = () => {
  const agentFinderChatBotWelcomeMessages: string =
    "Welcome! I'm your virtual assistant for agent recommendations. How can I assist you today?";
  const [isAgentFinderChatBotOpen, setIsAgentFinderChatBotOpen] = useState(false);
  const [agentFinderChatBotMessages, setAgentFinderChatBotMessages] = useState<ChatMessage[]>([
    {
      text: agentFinderChatBotWelcomeMessages,
      sender: 'bot',
    },
  ]);
  const [userInputMessage, setUserInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const uuId = useRef<string | null>(null);

  const [sendChatMessage, { isLoading: isChatbotResponding, error: AgentFinderChatBotError }] =
    useSendChatMessageMutation();

  useEffect(() => {
    scrollToBottom();
  }, [agentFinderChatBotMessages]);

  useEffect(() => {
    if (AgentFinderChatBotError) {
      showSnackbarErrorMessage(AgentFinderChatBotError as ApiError);
    }
  }, [AgentFinderChatBotError]);

  const addMessage = useCallback((message: ChatMessage) => {
    setAgentFinderChatBotMessages((prev) => [...prev, message]);
  }, []);

  const clearMessages = useCallback(() => {
    setAgentFinderChatBotMessages([
      {
        text: agentFinderChatBotWelcomeMessages,
        sender: 'bot',
      },
    ]);
    uuId.current = null;
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!userInputMessage.trim()) return;

    // Add user message
    addMessage({
      text: userInputMessage,
      sender: 'user',
    });

    const currentInput = userInputMessage;
    setUserInputMessage('');

    if (uuId.current === null) {
      uuId.current = crypto.randomUUID();
    }

    const sendChatMessageParams = { message: currentInput, session_id: uuId.current };
    const agentFinderChatBotResponse = await sendChatMessage(sendChatMessageParams).unwrap();

    if (agentFinderChatBotResponse && agentFinderChatBotResponse.success) {
      // Add bot message on success
      addMessage({
        text: agentFinderChatBotResponse.response,
        sender: 'bot',
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChatBotChat = () => {
    setIsAgentFinderChatBotOpen(!isAgentFinderChatBotOpen);
  };

  const handleChatBotCloseChat = () => {
    setIsAgentFinderChatBotOpen(false);
    clearMessages();
  };

  return (
    <>
      {/* Floating Chat Button */}
      <Tooltip
        title="Need help finding the right agent?"
        arrow
        placement="left"
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '16px',
              padding: '10px',
              borderRadius: '8px',
            },
          },
        }}
      >
        <Fab
          color="primary"
          aria-label="chat"
          onClick={toggleChatBotChat}
          className="chatbot-fab"
          sx={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1300,
            width: 65,
            height: 65,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <ChatIcon fontSize="large" />
        </Fab>
      </Tooltip>

      {/* Chat Dialog */}
      <Dialog
        open={isAgentFinderChatBotOpen}
        onClose={toggleChatBotChat}
        className="chatbot-dialog"
        PaperProps={{
          sx: {
            width: isMobile ? '95vw' : '400px',
            height: isMobile ? '80vh' : '600px',
            position: 'fixed',
            zIndex: 1300,
            bottom: isMobile ? '50px' : '100px',
            right: isMobile ? '50%' : '20px',
            transform: isMobile ? 'translateX(50%)' : 'none',
            margin: 0,
            borderRadius: 2,
            '&:hover': {
              boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
            },
            '&.MuiPaper-root': {
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
              borderRadius: '16px',
            },
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: 'transparent' },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <RedditIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Agent Finder Assistant
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={clearMessages} sx={{ color: 'white' }} title="Clear chat history">
              <Refresh />
            </IconButton>
            <IconButton size="small" onClick={handleChatBotCloseChat} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Messages Area */}
        <DialogContent
          sx={{
            flex: 1,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              backgroundColor: '#f5f5f5',
            }}
            className="chatbot-messages-container"
          >
            {agentFinderChatBotMessages.map((message, index) => (
              <Box
                key={index}
                className="chatbot-message"
                sx={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 1,
                    maxWidth: '90%',
                    flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: message.sender === 'user' ? '#1976d2' : '#9e9e9e',
                    }}
                  >
                    {message.sender === 'user' ? <Person fontSize="small" /> : <RedditIcon fontSize="small" />}
                  </Avatar>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1,
                      bgcolor: message.sender === 'user' ? '#1976d2' : '#ffffff',
                      color: message.sender === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                      wordWrap: 'break-word',
                      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                      overflow: 'hidden',
                      '& ul, & ol': {
                        paddingLeft: '16px',
                        margin: '8px 0',
                      },
                    }}
                  >
                    {message.sender === 'bot' ? (
                      <Box sx={{ fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}>
                        <MarkdownRenderer content={message.text} />
                      </Box>
                    ) : (
                      <Typography sx={{ fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}>
                        {message.text}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              </Box>
            ))}

            {/* Loading indicator */}
            {isChatbotResponding && (
              <Box
                className="chatbot-loading"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: '#9e9e9e' }}>
                    <RedditIcon fontSize="small" />
                  </Avatar>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      bgcolor: '#ffffff',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}
                    >
                      Typing...
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box
            className="chatbot-input-container"
            sx={{
              p: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: 'white',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder={'Type your message...'}
                variant="outlined"
                size="small"
                value={userInputMessage}
                onChange={(e) => setUserInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={isChatbotResponding}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!userInputMessage.trim() || isChatbotResponding}
                className="chatbot-send-button"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Send fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AgentFinderChatBot;
